// Six axes for a team's radar chart, every one derived from data that already exists — HEROES (core) for
// the first four, heroIcons.ts's trait tags for the last two — so a hero's numbers or traits changing
// moves the chart with no second table to keep in sync. Pure and DOM-free: radarChart.ts draws this.
import { HEROES, type HeroKind } from '../core/heroes.js';
import { heroIcons, type IconId } from './heroIcons.js';
import { t } from './i18n/index.js';

export type Axis = 'dmg' | 'hp' | 'mobility' | 'range' | 'control' | 'support';
export const AXES: Axis[] = ['dmg', 'hp', 'mobility', 'range', 'control', 'support'];
// Short deliberately: these sit at the rim of a small chart, not in a legend — a long label there is a
// clipped label waiting to happen (see radarChart.ts's quadrant anchoring, which assumes short text too).
export const axisLabel = (axis: Axis) => t(`axis.${axis}` as const);

// A gap-closer is worth roughly the roster's whole base-speed spread (~1 m/s) — a named, roster-derived
// rule, not a per-hero hand-tuned number.
const MOBILITY_DASH_BONUS = 1;

const ALL_HEROES = Object.keys(HEROES) as HeroKind[];

function hasTag(kind: HeroKind, tag: IconId): boolean {
  return heroIcons(kind).includes(tag);
}

function rawValue(kind: HeroKind, axis: Axis): number {
  const def = HEROES[kind];
  if (axis === 'dmg') return def.dmg / def.attackCd;
  if (axis === 'hp') return def.hp;
  if (axis === 'mobility') return def.speed + (hasTag(kind, 'dash') ? MOBILITY_DASH_BONUS : 0);
  if (axis === 'range') return def.range;
  if (axis === 'control') return (hasTag(kind, 'stun') ? 1 : 0) + (hasTag(kind, 'control') ? 1 : 0);
  return (hasTag(kind, 'heal') ? 1 : 0) + (hasTag(kind, 'shield') ? 1 : 0);  // 'support'
}

function rosterRange(axis: Axis): { min: number; max: number } {
  const values = ALL_HEROES.map(k => rawValue(k, axis));
  return { min: Math.min(...values), max: Math.max(...values) };
}
const RANGES = new Map<Axis, { min: number; max: number }>(AXES.map(a => [a, rosterRange(a)]));

/** This hero against the whole 12-hero roster on one axis, 0..1. The roster's own spread is the scale —
    every axis is guaranteed to reach 0 and 1 for some hero, by construction. */
export function normalizedValue(kind: HeroKind, axis: Axis): number {
  const { min, max } = RANGES.get(axis)!;
  if (max === min) return 0.5;  // no roster spread on this axis (not currently the case for any axis)
  return (rawValue(kind, axis) - min) / (max - min);
}

export type TeamProfile = Record<Axis, number>;

/** The mean of every filled seat's normalized profile (bots included, empty/unpicked seats excluded).
    `null` for a team with nobody picked yet — a collapsed shape at the centre would read as "weak at
    everything", not "nobody has chosen"; the caller must not draw one for a null profile. */
export function teamProfile(heroes: HeroKind[]): TeamProfile | null {
  if (heroes.length === 0) return null;
  const out = {} as TeamProfile;
  for (const axis of AXES) out[axis] = heroes.reduce((sum, k) => sum + normalizedValue(k, axis), 0) / heroes.length;
  return out;
}

/** The three strongest and three weakest axes, for a text alternative to the shape. */
export function strengthSummary(profile: TeamProfile): { strong: Axis[]; weak: Axis[] } {
  const sorted = [...AXES].sort((a, b) => profile[b] - profile[a]);
  return { strong: sorted.slice(0, 3), weak: sorted.slice(-3).reverse() };
}
